// Classic table pagination footer matching Optro's list tables: a
// rows-per-page select, the "start–end of total" range, and previous/next
// arrows. Always rendered, even for a single page, so short lists still show
// their total the way Optro does.
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

interface TablePaginationBarProps {
  /** 1-based current page. */
  page: number;
  rowsPerPage: number;
  /** Total rows across all pages, as reported by the server. */
  total: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  rowsPerPageOptions?: number[];
  /** Disables the controls while a page is in flight. */
  loading?: boolean;
}

export const TablePaginationBar = ({
  page,
  rowsPerPage,
  total,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  loading = false,
}: TablePaginationBarProps) => {
  const pageCount = Math.max(1, Math.ceil(total / rowsPerPage));
  const start = total === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const end = Math.min(page * rowsPerPage, total);

  return (
    <div className="flex items-center justify-end gap-4 px-4 py-2 text-sm text-stone-600">
      <div className="flex items-center gap-2">
        <span>Rows per page</span>
        <Select
          value={String(rowsPerPage)}
          onValueChange={(value) => onRowsPerPageChange(Number(value))}
          disabled={loading}
        >
          <SelectTrigger className="h-8 w-[76px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {rowsPerPageOptions.map((option) => (
              <SelectItem key={option} value={String(option)}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <span className="tabular-nums">
        {start}–{end} of {total}
      </span>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          disabled={loading || page <= 1}
          onClick={() => onPageChange(page - 1)}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          disabled={loading || page >= pageCount}
          onClick={() => onPageChange(page + 1)}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
